import { useEffect, useState } from "react";

/**
 * FofyMascot — animated SVG character for the FOFUS design assistant.
 * Lightweight pure SVG + CSS, no external deps.
 * States: idle (gentle float + blink), talking (mouth animates), thinking (sparks).
 */
export default function FofyMascot({ state = "idle" }: { state?: "idle" | "talking" | "thinking" }) {
  const [blink, setBlink] = useState(false);

  // Random blink every 3-5 seconds
  useEffect(() => {
    const loop = () => {
      setBlink(true);
      setTimeout(() => setBlink(false), 150);
      setTimeout(loop, 3000 + Math.random() * 2000);
    };
    const t = setTimeout(loop, 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fofy-mascot-wrapper" style={{ width: 64, height: 64, flexShrink: 0 }}>
      <svg viewBox="0 0 120 120" width="64" height="64" className={`fofy-mascot fofy-${state}`}>
        {/* Glow background when thinking */}
        {state === "thinking" && (
          <circle cx="60" cy="55" r="50" fill="#3b82f6" opacity="0.08">
            <animate attributeName="r" values="46;54;46" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.06;0.12;0.06" dur="1.5s" repeatCount="indefinite" />
          </circle>
        )}

        {/* Floating group */}
        <g className="fofy-float">
          {/* Body — rounded 3D-printer-bot shape */}
          <rect x="28" y="40" width="64" height="56" rx="16" fill="#2563eb" />
          <rect x="28" y="40" width="64" height="56" rx="16" fill="url(#bodyGrad)" />
          {/* Face screen */}
          <rect x="36" y="48" width="48" height="32" rx="10" fill="#eff6ff" />
          <rect x="36" y="48" width="48" height="32" rx="10" fill="url(#screenGrad)" />

          {/* Eyes */}
          <g className="fofy-eyes">
            {/* Left eye */}
            <circle cx="50" cy="62" r="5" fill="#1e40af">
              <animate attributeName="r" values={blink ? "0.5" : "5"} dur="0.15s" />
            </circle>
            {/* Right eye */}
            <circle cx="70" cy="62" r="5" fill="#1e40af">
              <animate attributeName="r" values={blink ? "0.5" : "5"} dur="0.15s" />
            </circle>
            {/* Eye sparkle */}
            {!blink && (
              <>
                <circle cx="51.5" cy="60.5" r="1.2" fill="#fff" />
                <circle cx="71.5" cy="60.5" r="1.2" fill="#fff" />
              </>
            )}
          </g>

          {/* Mouth — animates when talking */}
          {state === "talking" ? (
            <ellipse cx="60" cy="72" rx="5" ry="3.5" fill="#1e40af">
              <animate attributeName="ry" values="1;4;2;3.5;1.5;3;1" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="rx" values="5;4;5;3.5;5;4;5" dur="0.5s" repeatCount="indefinite" />
            </ellipse>
          ) : (
            <path d="M54 72 Q60 76 66 72" stroke="#1e40af" strokeWidth="2" fill="none" strokeLinecap="round" />
          )}

          {/* Build plate / feet */}
          <rect x="34" y="96" width="52" height="8" rx="4" fill="#1e40af" opacity="0.8" />
          <rect x="38" y="92" width="44" height="6" rx="3" fill="#3b82f6" opacity="0.5" />

          {/* Antenna with spark when thinking */}
          <line x1="60" y1="40" x2="60" y2="28" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
          {state === "thinking" ? (
            <g>
              <circle cx="60" cy="24" r="4" fill="#fbbf24">
                <animate attributeName="r" values="3;5;3" dur="0.8s" repeatCount="indefinite" />
              </circle>
              {/* Spark particles */}
              <circle cx="52" cy="20" r="1.5" fill="#fbbf24">
                <animate attributeName="cy" values="24;14;24" dur="1s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" />
              </circle>
              <circle cx="68" cy="20" r="1.5" fill="#fbbf24">
                <animate attributeName="cy" values="24;14;24" dur="1s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" begin="0.3s" />
              </circle>
            </g>
          ) : (
            <circle cx="60" cy="24" r="3" fill="#60a5fa" />
          )}

          {/* Cheek blush */}
          <circle cx="42" cy="68" r="2.5" fill="#f9a8d4" opacity="0.5" />
          <circle cx="78" cy="68" r="2.5" fill="#f9a8d4" opacity="0.5" />
        </g>

        {/* Gradients */}
        <defs>
          <linearGradient id="bodyGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="screenGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.3" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}